'use client'

import React, { useState, useRef, useEffect } from 'react';

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
);

interface Option {
    value: string;
    label: string;
}

interface CustomSelectProps {
    value: string;
    onChange: (value: string) => void;  // change here
    disabled?: boolean;
    options: Option[];
    className?: string;
    'aria-label'?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    value,
    onChange,
    disabled = false,
    options,
    className = "",
    'aria-label': ariaLabel
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (optionValue: string): void => {
        onChange(optionValue);   // just pass value
        setIsOpen(false);
    };

    const selectedOption = options.find(opt => opt.value === value);

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className="w-full appearance-none bg-gradient-to-br from-zinc-200 to-zinc-300 mb-[2px] text-zinc-800 rounded-full border border-zinc-600/50 pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:border-blue-400/60 cursor-pointer transition-all duration-200 hover:bg-gradient-to-br hover:from-zinc-750 hover:to-zinc-850 hover:border-zinc-500/60 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm shadow-lg text-left"
                aria-label={ariaLabel}
            >
                {selectedOption?.label || value}
            </button>

            {/* Chevron Icon */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-400 transition-all duration-200">
                <ChevronDownIcon
                    className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
            </div>

            {/* Dropdown Menu */}
            {isOpen && !disabled && (
                <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

                    {/* Menu */}
                    <div className="absolute bottom-full mb-1 z-[300] w-full bg-gradient-to-br from-zinc-200 to-zinc-300 border border-zinc-300/50 rounded-xl shadow-2xl backdrop-blur-xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">
                        {options.map((option, index) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className={`w-full cursor-pointer px-3 py-2.5 text-sm text-left transition-all duration-150 hover:bg-gradient-to-r hover:from-blue-600/20 hover:to-500-600/20 hover:text-gray-700 focus:outline-none focus:bg-gradient-to-r focus:from-blue-600/20 focus:to-blue-600/20 focus:text-white ${value === option.value
                                    ? 'bg-gradient-to-r from-blue-500/30 to-blue-600/30 text-black'
                                    : 'text-zinc-700'
                                    } ${index === 0 ? 'rounded-t-xl' : ''} ${index === options.length - 1 ? 'rounded-b-xl' : ''
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span>{option.label}</span>
                                    {value === option.value && (
                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </>
            )}

        </div>
    );
};

// Usage component with proper TypeScript typing
interface ModeSelectorProps {
    mode: string;
    onModeChange: (value: string) => void;  
    isLoading?: boolean;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ mode, onModeChange, isLoading = false }) => {
    const options: Option[] = [
        { value: 'Standard', label: 'Standard' },
        { value: 'Reason', label: 'Reason' }
    ];

    return (
        <CustomSelect
            value={mode}
            onChange={onModeChange}
            disabled={isLoading}
            options={options}
            aria-label="Select chat mode"
        />
    );
};

export { CustomSelect, ModeSelector };
export type { Option, CustomSelectProps, ModeSelectorProps };